import { Component, inject, Input, OnInit, OnDestroy, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CostTrackingService } from '../../../services/cost-tracking.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { AccountService } from '../../../../../_shared/services';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { AssetAssignmentFilterComponent } from '../../../../../_shared/components/asset-assignment-filter/asset-assignment-filter.component';
import { buildAssetTree } from '../../../../../_shared/helpers/hierarchy.helper';
import { DriverAssignmentFilter } from '../../../../../_shared/models';
import { UnifiedTreeItem } from '../../../../../_shared/models/driver.model';
import { BUDGET_CATEGORY_LABELS, BudgetCategory, CreateBudgetsRequest, UpdateBudgetRequest } from '../../../models/cost-tracking-request.model';
import { BudgetItem } from '../../../models/cost-tracking-response.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../../_shared/pipes/lookup-label.pipe';
import { translateLookupLabel } from '../../../../../_shared/helpers/lookup-label.helper';

@Component({
  selector: 'app-set-budget-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgbDropdownModule, TranslocoDirective, TranslocoPipe, LookupLabelPipe, AssetAssignmentFilterComponent],
  templateUrl: './set-budget-modal.component.html',
  styleUrl: './set-budget-modal.component.scss'
})
export class SetBudgetModalComponent implements OnInit, OnDestroy {
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private fb: FormBuilder = inject(FormBuilder);
  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private accountService: AccountService = inject(AccountService);
  private filterService: FilterService = inject(FilterService);
  private toastr: ToastrService = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  @Input() budget: BudgetItem | null = null;

  protected readonly budgetCategoryLabels = BUDGET_CATEGORY_LABELS;

  budgetForm: FormGroup;
  isLoadingCategories: boolean = true;
  isSaving: boolean = false;

  budgetCategories: BudgetCategory[] = [];
  selectedCategory: BudgetCategory | null = null;

  categoryConflictMessage: string | null = null;

  private static readonly budgetAlreadyExistsCode = 'cost-tracking/budget-already-exists';

  assetsTree: UnifiedTreeItem[] = [];
  assetsLoaded: WritableSignal<boolean> = signal(false);
  vehicleFilter: DriverAssignmentFilter = { drivers: [], assets: [] };

  private cancelSubject: Subject<void> = new Subject<void>();

  get isEditMode(): boolean {
    return !!this.budget;
  }

  constructor() {
    this.budgetForm = this.fb.group({
      annualBudget: ['', [Validators.required, Validators.min(0)]],
      monthlyBudget: ['', [Validators.required, Validators.min(0)]],
      notes: ['']
    });
  }

  ngOnInit(): void {
    if (this.budget) {
      // Seed from the row itself so the form is valid even if the catalogue fetch fails or the category was retired.
      this.selectedCategory = { id: this.budget.budgetCategoryId, name: this.budget.budgetCategoryName };
      this.budgetForm.patchValue({
        annualBudget: this.budget.annualBudget,
        monthlyBudget: this.budget.monthlyBudget,
        notes: this.budget.notes ?? ''
      });
    }

    this.loadBudgetCategories();

    if (!this.budget) {
      this.loadVehicleAccountTree();
    }
  }

  get editedAssetName(): string {
    return this.budget?.assetName ?? this.transloco.translate('costTracking.budgetTable.fleet');
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async loadBudgetCategories(): Promise<void> {
    this.isLoadingCategories = true;

    try {
      const response = await this.costTrackingService.getBudgetCategories();
      this.budgetCategories = response.data ?? [];

      if (this.budget) {
        this.selectedCategory = this.budgetCategories.find(c => c.id === this.budget!.budgetCategoryId) ?? this.selectedCategory;
      }

      this.isLoadingCategories = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.budgetCategories = [];
          this.isLoadingCategories = false;
        }
      });
    }
  }

  private async loadVehicleAccountTree(): Promise<void> {
    this.assetsLoaded.set(false);

    try {
      // An empty list means "all the user's accounts" for this endpoint.
      const accountIds = this.filterService.filter?.accountIds ?? [];
      const response = await this.accountService.getUserAccountAssets(accountIds, this.cancelSubject);

      if (response.data) {
        this.assetsTree = buildAssetTree(response.data);
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.assetsLoaded.set(true);
    }
  }

  onVehicleFilterChanged(filter: DriverAssignmentFilter): void {
    this.vehicleFilter = filter;
  }

  onCategorySelect(category: BudgetCategory): void {
    this.selectedCategory = category;
    this.categoryConflictMessage = null;
  }

  get categoryLabel(): string {
    return translateLookupLabel(this.transloco, this.budgetCategoryLabels, this.selectedCategory?.name)
      || this.transloco.translate('costTracking.setBudgetModal.selectCategory');
  }

  get hasVehicleSelection(): boolean {
    return this.isEditMode || this.vehicleFilter.assets.length > 0;
  }

  get isValid(): boolean {
    return !!(
      this.hasVehicleSelection &&
      this.selectedCategory &&
      this.budgetForm.get('annualBudget')?.valid &&
      this.budgetForm.get('monthlyBudget')?.valid
    );
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  async save(): Promise<void> {
    if (!this.isValid) return;

    const budgetCategoryId = this.selectedCategory!.id;
    const annualBudget = Number(this.budgetForm.get('annualBudget')?.value);
    const monthlyBudget = Number(this.budgetForm.get('monthlyBudget')?.value);
    const notes: string | null = this.budgetForm.get('notes')?.value || null;

    this.isSaving = true;
    this.categoryConflictMessage = null;

    try {
      if (this.budget) {
        const request: UpdateBudgetRequest = { budgetCategoryId, annualBudget, monthlyBudget, notes };
        await this.costTrackingService.updateBudget(this.budget.id, request);
      } else {
        const request: CreateBudgetsRequest = {
          assetIds: this.vehicleFilter.assets.map(a => a.id),
          budgetCategoryId,
          annualBudget,
          monthlyBudget,
          notes
        };
        await this.costTrackingService.createBudgets(request);
      }

      this.toastr.success(this.transloco.translate(this.isEditMode ? 'costTracking.setBudgetModal.toasts.updated' : 'costTracking.setBudgetModal.toasts.saved'));
      this.activeModal.close();
    } catch (error: any) {
      if (error?.error?.errorCode === SetBudgetModalComponent.budgetAlreadyExistsCode) {
        this.categoryConflictMessage = this.buildCategoryConflictMessage(error.error.message);
      } else {
        this.errorHandlerService.handleError(error);
      }
    } finally {
      this.isSaving = false;
    }
  }

  private buildCategoryConflictMessage(assetNames: string | undefined): string {
    const category = translateLookupLabel(this.transloco, this.budgetCategoryLabels, this.selectedCategory?.name)
      || this.selectedCategory?.name
      || '';

    if (this.budget) {
      const key = this.budget.assetId === null ? 'categoryExistsFleet' : 'categoryExists';
      return this.transloco.translate(`costTracking.setBudgetModal.errors.${key}`, { category });
    }

    return assetNames
      ? this.transloco.translate('costTracking.setBudgetModal.errors.categoryExistsFor', { category, assets: assetNames })
      : this.transloco.translate('costTracking.setBudgetModal.errors.categoryExists', { category });
  }

}